import Backbone from 'backbone'
import _ from 'lodash'
import calendars from 'scoreboard/components/calendars'
import dropdown from 'scoreboard/components/dropdown'
import selects from 'scoreboard/components/selects'
import periodReferenceTypeToggle from 'scoreboard/components/period-reference-type-toggle'
import RelativeOptions from 'scoreboard/components/relative-options'
import { validBreakdownCalendars, validGroupCalendars, getLargestCalendar }
	from './valid-group-and-breakdown-calendars'
import defaultTemplate from './grouped-period-range-selector.hbs'
import tooltipTemplate from './tooltip-grouped-period-range-selector.hbs'
import breakdownPeriodOptions from './breakdown-period-options'
import SummaryType from 'scoreboard/datasets/model/summary-type'
const {
	DAY_OF_THE_WEEK,
	MONTH_OF_THE_YEAR,
	SIMILAR_TIME_RANGES
} = SummaryType
const { DAILY, WEEKLY, MONTHLY, YEARLY, FISCAL_YEARLY } = calendars
import ChartCalendarLabels from './chart-calendar-labels'
const { mapCalendarsToCalendarsWithCustomLabels } = ChartCalendarLabels
import unwrapModel from 'scoreboard/components/unwrap-model'

const GroupedPeriodRangeSelector = Backbone.View.extend({
	className: 'grouped-period-range-selector',

	events: {
		'change [name=groupCalendarId]': 'onChangeGroupCalendarId',
		'change [name=breakdownCalendarId]': 'onChangeBreakdownCalendarId',
		'change [name=startPeriod]': 'onChangeStartPeriod',
		'change [name=endPeriod]': 'onChangeEndPeriod'
	},

	/**
	 * @param {String} options.model.attributes.summaryType
	 *
	 * @param {Number} [options.model.attributes.groupCalendarId] may be null when the
	 * summaryType is 'day-of-the-week' or 'month-of-the-year'
	 *
	 * @param {Number} [options.model.attributes.breakdownCalendarId] may be null when the
	 * summaryType is 'day-of-the-week' or 'month-of-the-year'
	 *
	 * @param {Boolean} [options.usePeriodRanges=true] enables/disable period
	 * range selection
	 *
	 * @param {Boolean} [allowUndefinedCalendars=true] see validGroupCalendars()
	 */
	initialize: function (options = {}) {
		this.options = _.extend({
			usePeriodRanges: true,
			allowUndefinedCalendars: true
		}, options)

		let periodType = _.isNil(this.model.get('startPeriodDelta'))
			? 'by-date' : 'relative'
		this.toggle = periodReferenceTypeToggle({ value: periodType })
		this.listenTo(this.toggle, 'change', () => this.onChangePeriodType())
		this.listenTo(this.model, 'change', () => this.trigger('change'))
		this._validCalendarsById = _.keyBy(
			validGroupCalendars({
				cals: null,
				allowUndefinedCalendars: this.options.allowUndefinedCalendars
			}),
			'id')

		this.initializeBreakdownCalendarId()
	},

	/**
	 * Initializes a valid breakdownCalendarId on the model when the summaryType
	 * is DAY_OF_THE_WEEK or MONTH_OF_THE_YEAR
	 */
	initializeBreakdownCalendarId: function() {
		let summaryType = this.summaryType()
		let newBreakdownCalendarId
		if (summaryType === DAY_OF_THE_WEEK) {
			newBreakdownCalendarId = getCalendarIdOfType(DAILY, calendars.calendars)
		} else if (summaryType === MONTH_OF_THE_YEAR) {
			newBreakdownCalendarId = getCalendarIdOfType(MONTHLY, calendars.calendars)
		}
		if (newBreakdownCalendarId) {
			this.model.set('breakdownCalendarId', newBreakdownCalendarId)
		}
	},

	render: function () {
		let template = this.options.tooltipTemplate ? tooltipTemplate() : defaultTemplate()
		this.$el.append(template)
		this.$el.toggleClass('toggler-rows', !this.options.tooltipTemplate)
		this.$el.toggleClass('tooltip-grouped-period-range-selector',
			!!this.options.tooltipTemplate)

		this.$('.toggle-container').append(this.toggle.el)
		this.toggle.render()

		this.renderGroupCalendarSelect()
		this.renderBreakdownCalendarSelect()
		this.renderPeriodSelects()

		return this
	},

	renderGroupCalendarSelect: function () {
		let calendars = validGroupCalendars({
			allowUndefinedCalendars: this.options.allowUndefinedCalendars
		})
		calendars = mapCalendarsToCalendarsWithCustomLabels(calendars)
		dropdown.idLabels(this.groupCalendarSelect().empty(),
			calendars, this.getGroupCalendarId(calendars))
	},

	renderBreakdownCalendarSelect: function () {
		var breakdownCalendars = validBreakdownCalendars(this.selectedGroupCalendarId())
		breakdownCalendars = mapCalendarsToCalendarsWithCustomLabels(breakdownCalendars)
		dropdown.idLabels(this.$('[name=breakdownCalendarId]').empty(),
			breakdownCalendars, this.getBreakdownCalendarId(breakdownCalendars))
	},

	/**
	 * @returns {Number|String} the ID of the breakdown calendar based on the
	 * current model
	 */
	getBreakdownCalendarId: function(breakdownCalendars) {
		let breakdownCalendarId = this.model.get('breakdownCalendarId')
		//if the current breakdown calendar is not value set it to the first one
		if (_.isNil(_.find(
			breakdownCalendars, cal => cal.id == breakdownCalendarId
		))) {
			breakdownCalendarId = breakdownCalendars[0]?.id
			this.model.set('breakdownCalendarId', breakdownCalendarId)
		}
		return breakdownCalendarId
	},

	renderPeriodSelects: function () {
		if (!this.options.usePeriodRanges) {
			return
		}
		this.$('.period, .period-type').toggleClass('is-not-displayed', false)
		let rangeType = this.toggle.val()

		let options
		if (rangeType === 'relative') {
			// Matches relative range options from v3 charts
			options = new RelativeOptions({
				start: -100,
				end: 100,
				calendar: calendars.calendarsById[this.model.get('breakdownCalendarId')]
			}).generate()
		} else {
			options = breakdownPeriodOptions(this.idsAndSummaryType())
		}

		let renderPeriodSelect = (startOrEnd) => {
			const propertyKey = `${startOrEnd}Period${this.indexOrDelta()}`

			// #36761 Don't allow the breakdown range to go outside of the available
			// options derived from the group calendar. i.e. There is no quarter 5.
			let value = this.model.get(propertyKey)
			if (parseInt(value) > options.length - 1) {
				this.model.set(propertyKey, options.length - 1)
			}
			if (value == -1) {
				value = 0
			}

			dropdown.idLabels(
				this.$(`[name=${startOrEnd}Period]`).empty(),
				options,
				value)
		}

		renderPeriodSelect('start')
		renderPeriodSelect('end')
	},

	onChangePeriodType: function () {
		this.setPeriodRangeDefaults()
		this.renderPeriodSelects()
	},

	/**
	 * #38908 Updates the start/end period so that the period dropdowns always
	 * have the first/last periods selected
	 */
	setPeriodRangeDefaults: function() {
		let newType = this.toggle.val()

		let update
		let possibleBreakdownPeriods = breakdownPeriodOptions(this.model.attributes)
		if (newType === 'by-date') {
			update = {
				startPeriodDelta: null,
				endPeriodDelta: null,
				// Default to using every breakdown period in the group period
				// (ie if Yearly group and Monthly breakdown, use Jan-Dec)
				startPeriodIndex: 0,
				endPeriodIndex: possibleBreakdownPeriods.length - 1
			}
		} else {
			update = {
				// Default to going back a full group period length
				// (ie if Yearly group and Monthly breakdown, use
				// 11 months ago to current period)
				startPeriodDelta: 1 - possibleBreakdownPeriods.length,
				endPeriodDelta: 0,
				startPeriodIndex: null,
				endPeriodIndex: null
			}
		}

		this.model.set(update)
	},

	/**
	 * @param {Object[]} [cals=calendars.calendars] the calendars
	 * to search for the ID
	 * @return the selected group calendar id in the model or the largest calendar
	 * 		option if none is selected or the current value uses an invalid calendar
	 */
	getGroupCalendarId: function(cals) {
		let summaryType = this.summaryType()
		if (summaryType === DAY_OF_THE_WEEK) {
			return getCalendarIdOfType(WEEKLY, cals)
		} else if (summaryType === MONTH_OF_THE_YEAR) {
			return getCalendarIdOfType(YEARLY, cals)
		}

		let groupCalendarId = this.model.get('groupCalendarId')
		return this._validCalendarsById[groupCalendarId] ? groupCalendarId :
			getLargestCalendar().id
	},

	onChangeGroupCalendarId: function () {
		const groupCalendarId = this.selectedGroupCalendarId()
		this.model.set('groupCalendarId', groupCalendarId)

		// #38328 Ensure the breakdown calendar is always set to something valid.
		// If not, set it to the largest calendar available
		let breakdownSelected = this.model.get('breakdownCalendarId')
		const validBreakdowns = validBreakdownCalendars(groupCalendarId)
		if (breakdownSelected && !validBreakdowns.find(v => v.id === breakdownSelected)) {
			let breakdownCalendarId = validBreakdowns[0].id
			this.model.set('breakdownCalendarId', breakdownCalendarId)

			// #49649 If we're changing the breakdown calendar, we need to change
			// the summaryType, too
			let summaryType = idsAndSummaryType(
				groupCalendarId, breakdownCalendarId)?.summaryType
			this.model.set('summaryType', summaryType)
		}
		this.setPeriodRangeDefaults()
		this.renderBreakdownCalendarSelect()
		this.renderPeriodSelects()

		// After several intermediate changes above, we need to trigger a 'change'
		// event at the end now that everything is updated
		this.trigger('change')
	},

	onChangeBreakdownCalendarId: function () {
		this.model.set({
			// #44167: We might be coming from a state where we have no group
			// calendar ID, so if a breakdown ID is selected, that implies default
			// group ID selection, and we need to set that to avoid NPEs.
			groupCalendarId: this.selectedGroupCalendarId(),
			breakdownCalendarId: this.selectedBreakdownCalendarId()
		})
		this.setPeriodRangeDefaults()
		this.renderPeriodSelects()
	},

	onChangeStartPeriod: function () {
		const start = this.selectedStartPeriod()
		this.model.set('startPeriod' + this.indexOrDelta(), start)
		this.ensureRangeStartsBeforeItEnds(start)
	},

	onChangeEndPeriod: function () {
		const end = this.selectedEndPeriod()
		this.model.set('endPeriod' + this.indexOrDelta(), end)
		this.ensureRangeStartsBeforeItEnds(end)
	},

	ensureRangeStartsBeforeItEnds: function (justSelected) {
		const start = this.selectedStartPeriod()
		const end = this.selectedEndPeriod()
		if (start > end) {
			this.startPeriodInput().val(justSelected)
			this.endPeriodInput().val(justSelected)
			this.model.set('startPeriod' + this.indexOrDelta(), justSelected)
			this.model.set('endPeriod' + this.indexOrDelta(), justSelected)
			selects.update(this.startPeriodInput())
			selects.update(this.endPeriodInput())
		}
	},

	indexOrDelta: function () {
		return this.toggle.val() === 'relative' ? 'Delta' : 'Index'
	},

	/**
	 * Returns the summaryType set on the model
	 */
	summaryType: function() {
		return unwrapModel(this.options.model)?.summaryType
	},

	groupCalendarSelect: function () {
		return this.$('[name=groupCalendarId]')
	},

	selectedGroupCalendarId: function () {
		return this.groupCalendarSelect().val()
	},

	breakdownCalendarSelect: function () {
		return this.$('[name=breakdownCalendarId]')
	},

	selectedBreakdownCalendarId: function () {
		return this.breakdownCalendarSelect().val()
	},

	startPeriodInput: function () {
		return this.$('[name=startPeriod]')
	},

	selectedStartPeriod: function () {
		return parseInt(this.startPeriodInput().val())
	},

	endPeriodInput: function () {
		return this.$('[name=endPeriod]')
	},

	selectedEndPeriod: function () {
		return parseInt(this.endPeriodInput().val())
	},

	/**
	 * Returns the groupCalendarId, breakdownCalendarId and summaryType
	 * specified by this view's controls
	 *
	 * @return {{groupCalendarId, breakdownCalendarId, summaryType: string}}
	 */
	idsAndSummaryType: function() {
		return idsAndSummaryType(this.selectedGroupCalendarId(),
			this.selectedBreakdownCalendarId())
	}

})

/**
 * Returns the calendar with the given type
 * @param {String} type the calendar type (see CalendarType.java)
 * @param {Object[]} [cals=calendars.calendars] the calendars
 * to search for the ID
 * @return {*}
 */
function getCalendarIdOfType(type, cals=calendars.calendars) {
	let calendar = _.find(cals, calendar => {
		if (type == YEARLY) {
			if (_.includes([YEARLY, FISCAL_YEARLY], calendar.type)) {
				return true
			}
		}
		return type === calendar.type
	})
	return calendar ? calendar.id : type
}

/**
 * Returns the groupCalendarId, breakdownCalendarId and summaryType specified
 * by the given groupCalendarId and breakdownCalendarId.
 *
 * @param {Number|String} groupCalendarId
 * @param {Number|String} breakdownCalendarId
 * @param {Object[]} [cals=calendars.calendars]
 * @return {{groupCalendarId, breakdownCalendarId, summaryType: string}}
 */
function idsAndSummaryType(groupCalendarId, breakdownCalendarId,
	cals = calendars.calendars) {
	let calsById = _.keyBy(cals, 'id')

	let groupCalendar = calsById[groupCalendarId]
	let groupCalendarType = groupCalendar?.type ?? groupCalendarId

	let breakdownCalendar = calsById[breakdownCalendarId]
	let breakdownCalendarType = breakdownCalendar?.type ?? breakdownCalendarId

	let summaryType
	if (groupCalendar && breakdownCalendar) {
		// Even if we *could* use a SummaryType like MONTH_OF_THE_YEAR, prefer
		// to use the real calendars when they are both defined
		summaryType = SIMILAR_TIME_RANGES
	} else if ([YEARLY, FISCAL_YEARLY].includes(groupCalendarType) &&
		MONTHLY == breakdownCalendarType) {
		summaryType = MONTH_OF_THE_YEAR
		groupCalendarId = null
		breakdownCalendarId = null
	} else if (WEEKLY == groupCalendarType && DAILY == breakdownCalendarType) {
		summaryType = DAY_OF_THE_WEEK
		groupCalendarId = null
		breakdownCalendarId = null
	} else {
		summaryType = SIMILAR_TIME_RANGES
	}

	return { summaryType, groupCalendarId, breakdownCalendarId }
}

/**
 * Creates a model suitable for use with the GroupedPeriodRangeSelector. The
 * model will have just 3 properties: groupCalendarId, breakdownCalendarId and
 * summaryType.
 *
 * @param modelOrAttrs the base model or object containing properties for the
 * groupCalendarId, breakdownCalendarId and summaryType
 * @return {Backbone.Model}
 */
function createModel(modelOrAttrs) {
	const modelAttrs = _.pick(unwrapModel(modelOrAttrs), 'groupCalendarId',
		'breakdownCalendarId', 'summaryType')
	const model = new Backbone.Model(modelAttrs)
	return model
}

Object.assign(GroupedPeriodRangeSelector, { getCalendarIdOfType,
	idsAndSummaryType, createModel })

export default GroupedPeriodRangeSelector

GroupedPeriodRangeSelector.__filename__ = 'components/designer/grouped-period-range-selector.js'
